import { useState } from 'react';

import {
  ActivitiesTab,
  NotificationTrayActivityItem,
} from './ActivitiesTab/ActivitiesTab';
import {
  NotificationsTab,
  NotificationTrayNotificationItem,
} from './NotificationsTab/NotificationsTab';
import { TrayShell, TrayTab } from './TrayShell';

export interface NotificationsTrayProps {
  notifications: NotificationTrayNotificationItem[];
  activities?: NotificationTrayActivityItem[];
  onClose: () => void;

  // Notifications tab callbacks
  onClearAll: () => void;
  onMarkAllAsRead: () => void;
  loadOlder: () => void;
  hasMore: boolean;
  loadingMore: boolean;

  // Activities tab callbacks
  onClearCompletedActivities: () => void;
  onLoadOlderActivities: () => void;
  hasMoreActivities: boolean;
  loadingMoreActivities?: boolean;

  // Tab control
  defaultTab?: TrayTab;
  activeTab?: TrayTab;
  onTabChange?: (tab: TrayTab) => void;
}

/**
 * @description NotificationsTray is a tabbed sidebar component that renders notifications and activities.
 * It supports switching between a Notifications tab (urgent and regular notifications) and an Activities tab
 * (user activity feed). It is purely presentational and does not handle data fetching.
 */
export function NotificationsTray({
  notifications,
  activities = [],
  onClose,
  onClearAll,
  onMarkAllAsRead,
  loadOlder,
  hasMore,
  loadingMore,
  onClearCompletedActivities,
  onLoadOlderActivities,
  hasMoreActivities = false,
  loadingMoreActivities = false,
  defaultTab = 'Notifications',
  activeTab: controlledActiveTab,
  onTabChange,
}: NotificationsTrayProps) {
  const [internalActiveTab, setInternalActiveTab] =
    useState<TrayTab>(defaultTab);

  const isControlled = controlledActiveTab !== undefined;
  const activeTab = isControlled ? controlledActiveTab : internalActiveTab;

  const handleTabChange = (newTab: TrayTab) => {
    if (isControlled) {
      onTabChange?.(newTab);
    } else {
      setInternalActiveTab(newTab);
      onTabChange?.(newTab);
    }
  };

  const renderTabContent = () => {
    if (activeTab === 'Activities') {
      return (
        <ActivitiesTab
          activities={activities}
          onClearCompleted={onClearCompletedActivities}
          onNavigate={onClose}
          loadOlder={onLoadOlderActivities}
          hasMore={hasMoreActivities}
          loadingMore={loadingMoreActivities}
        />
      );
    }

    return (
      <NotificationsTab
        notifications={notifications}
        onClearAll={onClearAll}
        onMarkAllAsRead={onMarkAllAsRead}
        loadOlder={loadOlder}
        hasMore={hasMore}
        loadingMore={loadingMore}
      />
    );
  };

  return (
    <TrayShell
      onClose={onClose}
      activeTab={activeTab}
      onTabChange={handleTabChange}
    >
      {renderTabContent()}
    </TrayShell>
  );
}
